/**
 * DayniteJs: Core theme toggle logic.
 * @module DayniteJs
 * @author Suhaib Muhammad Babangida <suhaibmuhd04@gmail.com>
 * @version 1.2.0
 */
import { getStoredTheme, storeTheme } from '../utils/storage.js';
import { isValidTheme } from '../utils/validation.js';
import { DEFAULT_THEMES, DEFAULT_THEME, DEFAULT_STORAGE_KEY } from '../utils/constants.js';
import { getSystemTheme } from '../utils/system.js';

/**
 * Main class for theme toggling and management.
 */
export default class DayniteJs {
    /**
     * Creates a new DayniteJs instance.
     * @param {Object} options - Configuration options.
     * @param {string[]} options.themes - Available themes (default: ['light', 'dark']).
     * @param {string} options.defaultTheme - Default theme (default: 'light').
     * @param {string} options.storageKey - localStorage key (default: 'DayniteJs-theme').
     * @param {Object} options.customStyles - Custom CSS variables per theme.
     */
    constructor(options = {}) {
        this.themes = Array.isArray(options.themes) && options.themes.length > 0 ? options.themes : DEFAULT_THEMES;
        this.defaultTheme = typeof options.defaultTheme === 'string' ? options.defaultTheme : DEFAULT_THEME;
        this.storageKey = typeof options.storageKey === 'string' ? options.storageKey : DEFAULT_STORAGE_KEY;
        this.customStyles = typeof options.customStyles === 'object' && options.customStyles !== null ? options.customStyles : {};
        this.callbacks = [];
        this._transitionTimer = null;
        this._mediaQueryListener = null;
        this._mediaQueryList = null;
        this.init();
    }

    /**
     * Initializes the library and applies the initial theme.
     */
    init() {
        if (!isValidTheme(this.defaultTheme, this.themes)) {
            console.warn(`Invalid defaultTheme "${this.defaultTheme}". Using '${DEFAULT_THEME}'.`);
            this.defaultTheme = DEFAULT_THEME;
        }
        const initialTheme = getStoredTheme(this.storageKey) || this.defaultTheme;
        this.setTheme(initialTheme);

        // Listen for system theme changes
        if (typeof window !== 'undefined' && window.matchMedia) {
            this._mediaQueryListener = (e) => {
                this.setTheme(e.matches ? 'dark' : 'light');
            };
            this._mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
            if (this._mediaQueryList.addEventListener) {
                this._mediaQueryList.addEventListener('change', this._mediaQueryListener);
            } else if (this._mediaQueryList.addListener) { // Fallback for older browsers
                this._mediaQueryList.addListener(this._mediaQueryListener);
            }
        }
    }

    /**
     * Destroys the instance and cleans up memory.
     */
    destroy() {
        if (this._mediaQueryList && this._mediaQueryListener) {
            if (this._mediaQueryList.removeEventListener) {
                this._mediaQueryList.removeEventListener('change', this._mediaQueryListener);
            } else if (this._mediaQueryList.removeListener) {
                this._mediaQueryList.removeListener(this._mediaQueryListener);
            }
        }
        if (this._transitionTimer) {
            clearTimeout(this._transitionTimer);
        }
        this.callbacks = [];
    }

    /**
     * Sets the active theme.
     * @param {string} theme - Theme to apply.
     */
    setTheme(theme) {
        if (!isValidTheme(theme, this.themes)) {
            console.warn(`Theme "${theme}" is not supported. Falling back to ${this.defaultTheme}.`);
            theme = this.defaultTheme;
        }

        if (typeof document === 'undefined') return;

        const applyTheme = () => {
            document.documentElement.classList.add('DayniteJs-transition');
            document.documentElement.setAttribute('data-theme', theme);
            // Add/remove Tailwind-compatible 'dark' class
            if (theme === 'dark') {
                document.documentElement.classList.add('dark');
            } else {
                document.documentElement.classList.remove('dark');
            }
            this.applyCustomStyles(theme);
            storeTheme(this.storageKey, theme);
            this.callbacks.forEach((cb) => cb(theme));

            if (this._transitionTimer) {
                clearTimeout(this._transitionTimer);
            }
            this._transitionTimer = setTimeout(() => {
                document.documentElement.classList.remove('DayniteJs-transition');
            }, 300);
        };

        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
            requestAnimationFrame(applyTheme);
        } else {
            applyTheme();
        }
    }

    /**
     * Returns the current theme.
     * @returns {string} The current theme name.
     */
    getTheme() {
        if (typeof document === 'undefined') return this.defaultTheme;
        return document.documentElement.getAttribute('data-theme') || this.defaultTheme;
    }

    /**
     * Resets the theme to the system or default theme.
     */
    reset() {
        const systemTheme = getSystemTheme();
        const fallbackTheme = isValidTheme(systemTheme, this.themes) ? systemTheme : this.defaultTheme;
        this.setTheme(fallbackTheme);
    }

    /**
     * Toggles to the next theme.
     */
    toggle() {
        const currentTheme = this.getTheme();
        if (!isValidTheme(currentTheme, this.themes)) {
            console.warn(`Current theme "${currentTheme}" is invalid. Falling back to ${this.defaultTheme}.`);
            this.setTheme(this.defaultTheme);
            return;
        }
        const currentIndex = this.themes.indexOf(currentTheme);
        const nextIndex = (currentIndex + 1) % this.themes.length;
        this.setTheme(this.themes[nextIndex]);
    }

    /**
     * Subscribes to theme change events.
     * @param {Function} callback - Called with the new theme.
     * @returns {Function} Unsubscribe function.
     */
    onThemeChange(callback) {
        if (typeof callback === 'function') {
            this.callbacks.push(callback);
            return () => {
                this.callbacks = this.callbacks.filter(cb => cb !== callback);
            };
        }
        return () => {};
    }

    /**
     * Applies custom CSS variables for the current theme.
     * @param {string} theme - Theme name.
     */
    applyCustomStyles(theme) {
        if (typeof document === 'undefined') return;
        const styles = this.customStyles[theme];
        if (styles && typeof styles === 'object') {
            Object.entries(styles).forEach(([key, value]) => {
                document.documentElement.style.setProperty(key, value);
            });
        }
    }
}
